// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Selections: the user's statement of which differences they mean.
 *
 * A selection is a named rectangle plus the corrections the user made to what it
 * caught. The rectangle is the coarse gesture — a real revision can differ in
 * tens of thousands of places, and no interface built on ticking those one by one
 * survives that. The corrections are the last mile: switch off a change the
 * rectangle swept up, switch on one that hangs over its edge.
 *
 * Nothing here decides whether a merge is safe. This module only answers "which
 * changes did the user point at"; {@link ../select/plan.ts} works out what taking
 * them actually entails.
 */

import type { DiffResult, ObjectChange } from '../diff/diff.ts';
import { classifyBox, type Containment, type Rect } from './rect.ts';

export interface Selection {
  /** Stable across re-renders and re-diffs; the UI keys rows on it. */
  id: string;
  /**
   * The user's name for it. "USB connector rework" survives an export and a
   * conversation in a way "Selection 3" does not.
   */
  name: string;
  rect: Rect;
  /** Changes inside the rectangle that the user switched off, by change key. */
  excluded: string[];
  /** Changes straddling the edge that the user switched on, by change key. */
  included: string[];
}

export function makeSelection(id: string, name: string, rect: Rect): Selection {
  return { id, name, rect, excluded: [], included: [] };
}

/** How one rectangle sees one change. */
export interface CaughtChange {
  change: ObjectChange;
  containment: Containment;
  /** Whether this change is actually taken, after the user's corrections. */
  taken: boolean;
}

export interface SelectionContents {
  /** Every change the rectangle touches at all, in diff order. */
  caught: CaughtChange[];
  /** Fully inside and not switched off. */
  taken: ObjectChange[];
  /** Hanging over the edge, or unmeasurable, and not switched on. */
  undecided: ObjectChange[];
}

/**
 * What a selection catches.
 *
 * A change's extent is the union of both sides' boxes (`ObjectChange.bbox`), so a
 * move counts as inside only when both where-it-was and where-it-went are inside.
 * That is the conservative reading: a footprint that moved out of the rectangle is
 * not something the rectangle can honestly claim to cover.
 */
export function selectionContents(diff: DiffResult, selection: Selection): SelectionContents {
  const excluded = new Set(selection.excluded);
  const included = new Set(selection.included);

  const caught: CaughtChange[] = [];
  const taken: ObjectChange[] = [];
  const undecided: ObjectChange[] = [];

  for (const change of diff.substantive) {
    const containment = classifyBox(change.bbox, selection.rect);
    if (containment === 'outside') continue;

    const isTaken =
      containment === 'inside' ? !excluded.has(change.key) : included.has(change.key);
    caught.push({ change, containment, taken: isTaken });
    if (isTaken) taken.push(change);
    else undecided.push(change);
  }

  return { caught, taken, undecided };
}

/** Every change taken across all selections. Overlapping rectangles union. */
export function takenChanges(diff: DiffResult, selections: readonly Selection[]): ObjectChange[] {
  const seen = new Set<string>();
  const out: ObjectChange[] = [];
  for (const selection of selections) {
    for (const change of selectionContents(diff, selection).taken) {
      if (seen.has(change.key)) continue;
      seen.add(change.key);
      out.push(change);
    }
  }
  return out;
}

/**
 * Which selections claim each change, in one pass.
 *
 * Overlapping rectangles are a union, not a sequence — taking a change twice is
 * taking it once — but the user still needs to be told, or deleting one selection
 * looks like it should drop a change it does not.
 *
 * Built as a map rather than answered per change on demand: the change list has a
 * row per change and a board can differ in tens of thousands of places, so a
 * per-row query would be quadratic.
 */
export function claimsByChange(
  diff: DiffResult,
  selections: readonly Selection[],
): Map<string, Selection[]> {
  const out = new Map<string, Selection[]>();
  for (const selection of selections) {
    for (const change of selectionContents(diff, selection).taken) {
      const claims = out.get(change.key);
      if (claims) claims.push(selection);
      else out.set(change.key, [selection]);
    }
  }
  return out;
}
